import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Settings,
    LogOut,
    CreditCard,
    LifeBuoy,
    HelpCircle,
    CodeXml,
    Crown,
    Star,
    Zap,
    BadgeCheck,
    ChevronsUpDown,
} from "lucide-react";
import { cn } from "lib/utils";
import { useSignOut, useProfileDetails, useCurrentSubscriptionPlan, useSubscriptionPlans } from "core";
import BrowsePlan from "components/support/plan/browse-plan/BrowsePlan";
import {
    Dropdown,
    DropdownTrigger,
    DropdownContent,
    DropdownItem,
    DropdownLabel,
    DropdownSeparator,
} from "components/ui/dropdown";
import { Skeleton } from "components/ui/skeleton";
import { getPlanTier, getPlanCTA, getDisplayName, getAvatarInitials, type PlanTier } from "./plan";

/* ══════════════════════════════════════════════════════════════════════════
   Account menu at the foot of the sidebar.

   The previous version hand-built a floating panel: manual
   `getBoundingClientRect` positioning, a `mousedown` listener for outside
   clicks, a portal, and two entirely separate render paths for the collapsed
   and expanded rail. It had no focus trap, no arrow-key navigation and no
   Escape handling.

   This is the same content on the shared Radix dropdown, so positioning,
   collision handling, focus management and keyboard support come for free —
   and the plan/upsell logic is untouched (see ./plan.ts).
   ══════════════════════════════════════════════════════════════════════════ */

const PLAN_ICON: Record<PlanTier, React.ElementType> = {
    pro: Crown,
    basic: Star,
    payg: Zap,
    free: BadgeCheck,
};

export const UserMenu: React.FC<{ collapsed: boolean; onNavigate: () => void }> = ({
    collapsed,
    onNavigate,
}) => {
    const navigate = useNavigate();
    const { data: profile, isLoading, isError } = useProfileDetails();

    /* A skeleton is a promise that something is coming. If the request failed,
       nothing is coming, and shimmering forever is a lie — so failure resolves
       to the account's real identity instead. `getDisplayName` already falls
       back to the email local part, and to "User" when even that is missing. */
    const showSkeleton = isLoading && !isError;
    const { data: currentPlan, isLoading: planLoading } = useCurrentSubscriptionPlan();
    const { data: apiPlans } = useSubscriptionPlans();
    const { mutate: signOut } = useSignOut();
    const [browsePlanOpen, setBrowsePlanOpen] = useState(false);

    const email = profile?.email ?? "";
    const displayName = getDisplayName(profile);
    const initials = getAvatarInitials(profile);
    const planTier = getPlanTier(currentPlan, apiPlans);
    const planCTA = getPlanCTA(planTier);
    const PlanIcon = PLAN_ICON[planTier];

    const go = (path: string) => {
        onNavigate();
        navigate(path);
    };

    const handleSignOut = () => {
        signOut();
        navigate("/");
    };

    const avatar = (
        <span
            className={cn(
                "relative flex-shrink-0 inline-flex items-center justify-center rounded-xl text-white font-extrabold tracking-wide",
                "bg-gradient-to-br from-brand-500 via-brand-600 to-brand-800 shadow-md",
                collapsed ? "w-10 h-10 text-ui" : "w-9 h-9 text-meta"
            )}
        >
            {showSkeleton ? <Skeleton className="w-4 h-4 rounded" animation="pulse" /> : initials}
            <span
                className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-success-solid border-2 border-[#111827]"
                aria-label="Online"
            />
        </span>
    );

    return (
        <>
            <Dropdown>
                <DropdownTrigger asChild>
                    <button
                        type="button"
                        aria-label={`Account menu for ${displayName}`}
                        className={cn(
                            "group w-full flex items-center rounded-xl transition-colors duration-150",
                            "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#111827]",
                            "hover:bg-white/[0.07] data-[state=open]:bg-white/[0.09]",
                            collapsed ? "justify-center p-1" : "gap-2.5 px-2 py-2"
                        )}
                    >
                        {avatar}

                        {!collapsed && (
                            <>
                                <span className="flex-1 min-w-0 text-left">
                                    {showSkeleton ? (
                                        <span className="block space-y-1.5 py-0.5">
                                            <Skeleton className="h-3 w-24 rounded" animation="pulse" />
                                            <Skeleton className="h-2.5 w-16 rounded" animation="pulse" />
                                        </span>
                                    ) : (
                                        <>
                                            <span className="block text-ui font-semibold text-white leading-tight truncate">
                                                {displayName}
                                            </span>
                                            <span className="block text-micro text-slate-400 leading-tight truncate mt-0.5">
                                                {email}
                                            </span>
                                        </>
                                    )}
                                </span>
                                <ChevronsUpDown
                                    className="w-4 h-4 flex-shrink-0 text-slate-500 group-hover:text-slate-300 transition-colors"
                                    aria-hidden="true"
                                />
                            </>
                        )}
                    </button>
                </DropdownTrigger>

                <DropdownContent
                    side="top"
                    align={collapsed ? "start" : "center"}
                    sideOffset={10}
                    /* No `overflow-hidden`: it overrode the scroll from the
                       shared dropdown, so on a short viewport the menu clipped
                       with no way to reach what was cut off. The base's
                       overflow-y-auto still clips to the rounded corners. */
                    className="w-[16.5rem] p-0"
                >
                    {/* ── Identity ──
                        Only when the rail is collapsed. Expanded, the trigger
                        directly below already shows the same name and email, and
                        the menu opens right on top of it — so this repeated both
                        lines a few pixels above themselves. */}
                    {collapsed && (
                        <div className="px-3 py-3 border-b border-line bg-surface-muted">
                            <p className="text-ui font-semibold text-content truncate">{displayName}</p>
                            <p className="text-micro text-content-muted truncate mt-0.5">{email}</p>
                        </div>
                    )}

                    {/* ── Plan + upsell. The single place a user sees which tier
                           they are on, so the CTA lives right next to it. ── */}
                    <div className="px-3 py-3 border-b border-line flex items-center gap-2.5">
                        {planLoading ? (
                            <div className="flex items-center gap-2.5 w-full">
                                <Skeleton className="w-8 h-8 rounded-xl flex-shrink-0" />
                                <div className="flex-1 space-y-1.5">
                                    <Skeleton className="h-3 w-20 rounded" />
                                    <Skeleton className="h-2.5 w-28 rounded" />
                                </div>
                            </div>
                        ) : (
                            <>
                                <span
                                    className={cn(
                                        "flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-xl",
                                        planTier === "pro"
                                            ? "bg-violet-subtle text-violet"
                                            : planTier === "payg"
                                                ? "bg-warning-subtle text-warning"
                                                : "bg-brand-subtle text-brand"
                                    )}
                                    aria-hidden="true"
                                >
                                    <PlanIcon className="w-4 h-4" strokeWidth={2} />
                                </span>
                                <div className="min-w-0 flex-1">
                                    <p className="text-meta font-bold text-content leading-tight truncate">
                                        {planCTA.label}
                                    </p>
                                    <p className="text-micro text-content-muted leading-tight truncate mt-0.5">
                                        {planCTA.sublabel}
                                    </p>
                                </div>
                                {planCTA.buttonLabel ? (
                                    <button
                                        type="button"
                                        onClick={() => setBrowsePlanOpen(true)}
                                        className="flex-shrink-0 text-micro font-bold px-2.5 py-1.5 rounded-lg bg-brand text-brand-fg hover:bg-brand-hover transition-colors focus-ring"
                                    >
                                        {planCTA.buttonLabel}
                                    </button>
                                ) : (
                                    <span className="flex-shrink-0 inline-flex items-center gap-1 text-2xs font-bold px-2 py-1 rounded-full bg-success-subtle text-success-on-subtle border border-success-border">
                                        <span className="w-1.5 h-1.5 rounded-full bg-success-solid" aria-hidden="true" />
                                        Active
                                    </span>
                                )}
                            </>
                        )}
                    </div>

                    <div className="p-1">
                        <DropdownLabel>Account</DropdownLabel>
                        <DropdownItem onSelect={() => go("/profile")}>
                            <Settings className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                            Profile &amp; Settings
                        </DropdownItem>
                        <DropdownItem onSelect={() => go("/payments")}>
                            <CreditCard className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                            Billing &amp; Plans
                        </DropdownItem>
                        <DropdownItem onSelect={() => go("/developer")}>
                            <CodeXml className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                            Developer Settings
                        </DropdownItem>

                        <DropdownSeparator />

                        <DropdownLabel>Support</DropdownLabel>
                        <DropdownItem onSelect={() => go("/support")}>
                            <LifeBuoy className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                            Support
                        </DropdownItem>
                        <DropdownItem onSelect={() => go("/help")}>
                            <HelpCircle className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                            Help &amp; Docs
                        </DropdownItem>

                        <DropdownSeparator />

                        <DropdownItem variant="destructive" onSelect={handleSignOut}>
                            <LogOut className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                            Log out
                        </DropdownItem>
                    </div>
                </DropdownContent>
            </Dropdown>

            <BrowsePlan
                open={browsePlanOpen}
                onClose={() => setBrowsePlanOpen(false)}
                currentPlanInfo={
                    currentPlan ? { subscriptionPlanId: currentPlan.subscriptionPlanId } : undefined
                }
                onSubscribe={() => setBrowsePlanOpen(false)}
            />
        </>
    );
};

export default UserMenu;
